/**
 * PDF Export Hook - High-Quality HTML to PDF Conversion
 *
 * Written by Claude (AI Assistant)
 *
 * OVERVIEW:
 * This hook converts HTML elements (specifically AI chat messages) into downloadable PDF files.
 *
 * KEY CONCEPTS:
 *
 * 1. HIGH-QUALITY RENDERING STRATEGY:
 *    - Captures HTML at 2x resolution (scale: 2) for crisp text and graphics
 *    - Scales PDF dimensions back down by the same factor to maintain original size
 *
 * 2. SMART PAGE BREAKING:
 *    - Uses whitespace detection to find optimal page break points
 *    - Avoids cutting through text or important content
 *    - Analyzes image data to identify whitespace-heavy rows for clean breaks
 *
 * 3. CONTENT PREPARATION:
 *    - Creates a temporary off-screen container to measure and prepare content
 *    - Constrains width to fit PDF page dimensions while preserving aspect ratio
 *    - Ensures fonts are loaded before capture for consistent rendering
 *
 * 4. MULTI-PAGE HANDLING:
 *    - Automatically splits content across multiple PDF pages when needed
 *    - Each page slice is rendered separately to maintain quality
 *    - Uses intelligent break point detection to avoid awkward splits
 *
 * TECHNICAL APPROACH:
 * - html2canvas: Converts DOM elements to high-resolution canvas
 * - jsPDF: Generates PDF from canvas data with precise dimension control
 * - Canvas manipulation: Analyzes and slices large content for pagination
 *
 * QUALITY OPTIMIZATIONS:
 * - 2x scale capture with dimension compensation
 * - Maximum PNG quality (1.0) for lossless compression
 * - Font loading synchronization
 * - Proper CORS and taint handling for external resources
 *
 * TRADE-OFFS & LIMITATIONS:
 *
 * ⚠️  TEXT NOT SELECTABLE: The biggest limitation of this approach is that all content
 *     becomes rasterized images, making text non-selectable and non-searchable in the PDF.
 *     This is an inherent limitation of the html2canvas → image → PDF pipeline.
 *
 * ⚠️  FILE SIZE: Higher quality images result in larger PDF file sizes compared to
 *     text-based PDFs.
 *
 * ⚠️  ACCESSIBILITY: Screen readers cannot parse image-based text content.
 *
 * ⚠️  BROWSER PERFORMANCE: High-resolution canvas operations can be memory intensive
 *     for very large content.
 *
 * ALTERNATIVE APPROACHES CONSIDERED:
 * - PDF text generation libraries: Complex to implement with existing styled content
 * - Server-side PDF generation: Would require significant backend changes
 *
 */

import { useCallback } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { telemetry } from '../../../../telemetry';

const PDF_CONFIG = {
  padding: 15,
  pixelsToMm: 25.4 / 96,
  format: 'a4' as const,
  orientation: 'p' as const,
  whitespaceThreshold: 0.95,
  searchRange: 100,
  layoutDelay: 100,
  // High quality settings
  scale: 2, // Capture at 2x resolution for better quality
  imageQuality: 1.0, // Maximum quality
} as const;

const TEMP_CONTAINER_STYLES = {
  position: 'absolute',
  top: '-9999px',
  left: '-9999px',
  height: 'auto',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  lineHeight: '1.5',
  color: '#000',
  backgroundColor: '#fff',
} as const;

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(new Error(`Failed to load image: ${error}`));
    img.src = src;
  });
};

const isWhitespaceRow = (imageData: ImageData, y: number, width: number): boolean => {
  const rowStart = y * width * 4;
  let whitePixels = 0;

  for (let x = 0; x < width; x++) {
    const pixelStart = rowStart + x * 4;
    const [r, g, b] = [
      imageData.data[pixelStart],
      imageData.data[pixelStart + 1],
      imageData.data[pixelStart + 2],
    ];

    if (r > 240 && g > 240 && b > 240) {
      whitePixels++;
    }
  }

  return whitePixels / width > PDF_CONFIG.whitespaceThreshold;
};

const findBetterBreakPoint = async (
  targetY: number,
  imgData: string,
  imgWidth: number,
  imgHeight: number,
): Promise<number> => {
  const searchRange = PDF_CONFIG.searchRange;

  // Create analysis canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return targetY;

  const startY = Math.max(0, targetY - searchRange);
  const analyzeHeight = Math.min(searchRange * 2, imgHeight - startY);

  canvas.width = imgWidth;
  canvas.height = analyzeHeight;

  try {
    // Load and analyze image
    const img = await loadImage(imgData);
    ctx.drawImage(img, 0, startY, imgWidth, analyzeHeight, 0, 0, imgWidth, analyzeHeight);

    const imageData = ctx.getImageData(0, 0, imgWidth, analyzeHeight);
    const centerOffset = Math.min(searchRange, targetY);

    // Search for whitespace above target first
    for (let offset = 0; offset < centerOffset; offset++) {
      const checkY = centerOffset - offset;
      if (checkY >= 0 && isWhitespaceRow(imageData, checkY, imgWidth)) {
        return startY + checkY;
      }
    }

    // Search below target if nothing found above
    for (let offset = 0; offset < analyzeHeight - centerOffset; offset++) {
      const checkY = centerOffset + offset;
      if (checkY < analyzeHeight && isWhitespaceRow(imageData, checkY, imgWidth)) {
        return startY + checkY;
      }
    }

    return targetY; // No good break point found
  } catch (error) {
    // If image loading fails, return the original target position
    return targetY;
  }
};

const addPdfSpecificStyles = (element: HTMLElement): void => {
  // Hide reasoning sections
  const reasoningSections = element.querySelectorAll('.reasoning-section');
  reasoningSections.forEach((section) => {
    (section as HTMLElement).style.display = 'none';
  });

  // Hide buttons bar
  const buttonsBars = element.querySelectorAll('.buttons-bar');
  buttonsBars.forEach((bar) => {
    (bar as HTMLElement).style.display = 'none';
  });

  const userMessages = element.querySelectorAll('[data-message-type="user"]');
  userMessages.forEach((userMsg) => {
    const userElement = userMsg as HTMLElement;
    userElement.style.marginBottom = '16px';
    userElement.style.display = 'flex';
    userElement.style.flexDirection = 'column';
    userElement.style.alignItems = 'flex-end';
    userElement.style.width = '100%';
  });

  const userMessageBoxes = element.querySelectorAll('.user-message-box');
  userMessageBoxes.forEach((box) => {
    const boxElement = box as HTMLElement;

    // Check if this user message has a replyTo by looking for the specific className
    const hasReplyTo = boxElement.querySelector('.user-message-reply-to') !== null;

    if (hasReplyTo) {
      // Style the entire user message container to include replyTo
      boxElement.style.width = 'fit-content';
      boxElement.style.maxWidth = '70%';
      boxElement.style.backgroundColor = '#f5f5f5';
      boxElement.style.border = '1px solid #e0e0e0';
      boxElement.style.borderRadius = '8px 8px 2px 8px';
      boxElement.style.padding = '8px 16px 0';
      boxElement.style.boxSizing = 'border-box';
      boxElement.style.marginLeft = 'auto';
      boxElement.style.display = 'flex';
      boxElement.style.flexDirection = 'column';
      boxElement.style.gap = '8px';
    } else {
      // Style the message box directly when there's no replyTo
      boxElement.style.width = 'fit-content';
      boxElement.style.maxWidth = '70%';
      boxElement.style.backgroundColor = '#f5f5f5';
      boxElement.style.border = '1px solid #e0e0e0';
      boxElement.style.borderRadius = '8px 8px 2px 8px';
      boxElement.style.padding = '0 16px';
      boxElement.style.boxSizing = 'border-box';
      boxElement.style.marginLeft = 'auto';
    }
  });

  // Style replyTo content specifically to be smaller and lighter in PDF
  const replyToElements = element.querySelectorAll('.user-message-reply-to');
  replyToElements.forEach((replyTo) => {
    const replyToElement = replyTo as HTMLElement;
    replyToElement.style.fontSize = '12px';
    replyToElement.style.color = '#666666';
    replyToElement.style.opacity = '0.8';
  });

  // Style assistant messages
  const assistantMessages = element.querySelectorAll('[data-message-type="assistant"]');
  assistantMessages.forEach((assistantMsg) => {
    const assistantElement = assistantMsg as HTMLElement;
    assistantElement.style.marginBottom = '16px';
    assistantElement.style.width = '100%';
  });

  // Style message content boxes using the dedicated class
  const messageContentBoxes = element.querySelectorAll('.message-content-box');
  messageContentBoxes.forEach((box) => {
    const boxElement = box as HTMLElement;
    boxElement.style.width = '100%';
  });

  // Hide any remaining interactive elements using JavaScript
  const buttons = element.querySelectorAll('button, [role="button"]');
  buttons.forEach((button) => {
    (button as HTMLElement).style.display = 'none';
  });

  const collapseElements = element.querySelectorAll('.MuiCollapse-root');
  collapseElements.forEach((collapse) => {
    (collapse as HTMLElement).style.display = 'none';
  });
};

const createTempContainer = (element: HTMLElement): HTMLDivElement => {
  const container = document.createElement('div');

  // Calculate max content width that fits on PDF page
  const pdf = new jsPDF(PDF_CONFIG.orientation, 'mm', PDF_CONFIG.format);
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const maxContentWidth = pdfWidth - PDF_CONFIG.padding * 2;
  const maxWidthInPixels = maxContentWidth / PDF_CONFIG.pixelsToMm;

  Object.entries(TEMP_CONTAINER_STYLES).forEach(([key, value]) => {
    (container.style as any)[key] = value;
  });

  // Constrain width to fit PDF page, but don't make it smaller than needed
  const elementWidth = element.scrollWidth;
  const constrainedWidth = Math.min(elementWidth, maxWidthInPixels);
  container.style.width = `${constrainedWidth}px`;
  container.style.wordWrap = 'break-word';
  container.style.overflowWrap = 'break-word';

  const cloned = element.cloneNode(true) as HTMLElement;
  cloned.style.fontFamily = 'Inter, sans-serif';
  cloned.style.wordWrap = 'break-word';
  cloned.style.overflowWrap = 'break-word';

  addPdfSpecificStyles(cloned);

  container.appendChild(cloned);
  return container;
};

const renderSinglePage = (pdf: jsPDF, imgData: string, width: number, height: number): void => {
  pdf.addImage(imgData, 'PNG', PDF_CONFIG.padding, PDF_CONFIG.padding, width, height);
};

const renderPageSlice = async (
  pdf: jsPDF,
  imgData: string,
  currentY: number,
  endY: number,
  imgWidth: number,
  actualWidth: number,
): Promise<void> => {
  const sourceHeight = endY - currentY;
  const pageDisplayHeight = (sourceHeight * PDF_CONFIG.pixelsToMm) / PDF_CONFIG.scale;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  canvas.width = imgWidth;
  canvas.height = sourceHeight;

  try {
    const img = await loadImage(imgData);
    ctx.drawImage(img, 0, currentY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);

    const sliceData = canvas.toDataURL('image/png', PDF_CONFIG.imageQuality);
    pdf.addImage(
      sliceData,
      'PNG',
      PDF_CONFIG.padding,
      PDF_CONFIG.padding,
      actualWidth,
      pageDisplayHeight,
    );
  } catch (error) {
    throw new Error(`Failed to render PDF page slice: ${error}`);
  }
};

export const usePdfExport = () => {
  const exportToPdf = useCallback(async (element: HTMLElement, chatName?: string) => {
    if (!element) {
      telemetry.captureError(new Error('Element not found for PDF export'));
      return false;
    }

    // Ensure fonts are loaded
    await document.fonts.ready;
    if (!document.fonts.check('12px Inter')) {
      await document.fonts.load('12px Inter');
    }

    // Create and setup temporary container
    const tempContainer = createTempContainer(element);
    document.body.appendChild(tempContainer);

    try {
      // Wait for layout to settle
      await new Promise((resolve) => setTimeout(resolve, PDF_CONFIG.layoutDelay));

      // Capture as canvas with high quality
      const canvas = await html2canvas(tempContainer, {
        scale: PDF_CONFIG.scale,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
        ignoreElements: (el) => el.tagName === 'SCRIPT' || el.tagName === 'STYLE',
        logging: false,
        windowWidth: tempContainer.scrollWidth || tempContainer.offsetWidth || 800,
        windowHeight: tempContainer.scrollHeight || tempContainer.offsetHeight || 600,
      });

      // Setup PDF
      const pdf = new jsPDF(PDF_CONFIG.orientation, 'mm', PDF_CONFIG.format);
      const contentHeight = pdf.internal.pageSize.getHeight() - PDF_CONFIG.padding * 2;

      // Calculate dimensions - divide by scale to maintain same size but higher quality
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const actualWidth = (imgWidth * PDF_CONFIG.pixelsToMm) / PDF_CONFIG.scale;
      const actualHeight = (imgHeight * PDF_CONFIG.pixelsToMm) / PDF_CONFIG.scale;

      const imgData = canvas.toDataURL('image/png', PDF_CONFIG.imageQuality);

      // Check if single page is sufficient
      if (actualHeight <= contentHeight) {
        renderSinglePage(pdf, imgData, actualWidth, actualHeight);
      } else {
        // Multi-page rendering with smart breaks
        let currentY = 0;
        let pageNum = 0;
        const pageHeightInPixels = (contentHeight * PDF_CONFIG.scale) / PDF_CONFIG.pixelsToMm;

        while (currentY < imgHeight) {
          if (pageNum > 0) pdf.addPage();

          const idealEndY = Math.min(currentY + pageHeightInPixels, imgHeight);
          const actualEndY =
            idealEndY >= imgHeight
              ? imgHeight
              : await findBetterBreakPoint(idealEndY, imgData, imgWidth, imgHeight);

          await renderPageSlice(pdf, imgData, currentY, actualEndY, imgWidth, actualWidth);

          currentY = actualEndY;
          pageNum++;
        }
      }

      // Generate filename and save
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const sanitizedName =
        chatName?.replace(/[^a-zA-Z0-9\s-_]/g, '').replace(/\s+/g, '_') || 'ai_message';

      pdf.save(`${sanitizedName}_${timestamp}.pdf`);
      return true;
    } catch (error) {
      telemetry.captureError(new Error('PDF export failed'), error);
      return false;
    } finally {
      // Cleanup
      if (document.body.contains(tempContainer)) {
        document.body.removeChild(tempContainer);
      }
    }
  }, []);

  return { exportToPdf };
};
